import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Stack, Alert, Button } from '@mui/material';
import { Page, PageTitle, Sheet } from '../../components';
import useFetch from '../../hooks/useFetch';
import OutflowChip from './OutflowChip';
import VendorChecksFlow from './VendorChecksFlow';
import { formatCents } from './money';
import { formatDay, todayET } from './dates';

export default function VendorPage({ auth, setAuth }) {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { fetchResource, showAlert, alertMessage } = useFetch({ setAuth });
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [flow, setFlow] = useState(null);
  const perms = auth?.perms || {};

  const load = () => fetchResource({ method: 'GET', resource: `calendar/vendors/${id}` }).then(r => !r.error && setData(r));

  useEffect(() => { load(); }, [fetchResource, id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchResource({ method: 'GET', resource: 'calendar/bank-accounts' }).then(r => !r.error && setAccounts(r.accounts || []));
  }, [fetchResource]);

  if (!data) return <Page>{showAlert && <Alert severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>}</Page>;

  return (
    <Page>
      <PageTitle>{data.vendor.name}</PageTitle>
      {showAlert && <Alert severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>}
      {(data.balances || []).map(b => (
        <Card key={b.bankAccountId} sx={{ mb: 1 }}>
          <CardContent>
            <Typography variant='overline'>{accounts.find(a => a.id === b.bankAccountId)?.nickname}</Typography>
            <Typography>{t('calendar.vendorChecks.owed')}: {formatCents(b.owedCents)}</Typography>
            <Typography variant='body2'>{t('calendar.vendorChecks.notCovered')}: {formatCents(b.notCoveredCents)}</Typography>
            {perms.CAL_ENTER && data.vendor.active && (
              <Stack data-testid='vendor-balance-actions' direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <Button size='small' onClick={() => setFlow({ bankAccountId: b.bankAccountId, action: 'set' })}>{t('calendar.vendorChecks.setOwed')}</Button>
                <Button size='small' onClick={() => setFlow({ bankAccountId: b.bankAccountId, action: 'add' })}>{t('calendar.vendorChecks.addOwed')}</Button>
                <Button size='small' onClick={() => setFlow({ bankAccountId: b.bankAccountId, action: 'checks' })}>{t('calendar.vendorChecks.addChecks')}</Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      ))}
      <Typography variant='subtitle1'>{t('calendar.vendorPage.open')}</Typography>
      {data.invoices.map(inv => (
        <Card key={inv.id} sx={{ mb: 1 }}>
          <CardContent>
            <Typography>
              {inv.kind === 'opening_balance' ? t('calendar.vendorChecks.openingBalance') : (inv.invoice_number || '—')} · {formatDay(inv.invoice_date, i18n.language)} · {formatCents(inv.total_cents)}
            </Typography>
            <Typography variant='body2'>
              {t('calendar.vendorPage.paid')}: {formatCents(inv.summary.paidCents)} · {t('calendar.vendorPage.scheduled')}: {formatCents(inv.summary.scheduledCents)}
              {inv.summary.uncoveredCents !== 0 && ` · ${t('calendar.vendorPage.uncovered')}: ${formatCents(inv.summary.uncoveredCents)}`}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>{inv.checks.map(c => <OutflowChip key={c.id} item={c} />)}</Stack>
          </CardContent>
        </Card>
      ))}
      {flow && (
        <Sheet open onClose={() => setFlow(null)} title={t('calendar.vendorChecks.title')}>
          <VendorChecksFlow date={todayET()} accounts={accounts} setAuth={setAuth}
            vendorId={Number(id)} bankAccountId={flow.bankAccountId} action={flow.action}
            onCancel={() => setFlow(null)}
            onChanged={load}
            onSaved={() => { setFlow(null); load(); }} />
        </Sheet>
      )}
    </Page>
  );
}
